/**
 * Input primitives — Input, Textarea, Label, FormField.
 *
 * Style: thin border, no shadow, focus ring via the global :focus-visible rule.
 * Error state adds a red border + red helper text.
 */
import {
  cloneElement,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

const inputBase =
  'block w-full rounded-md border bg-surface px-3 text-sm text-primary placeholder:text-tertiary disabled:bg-subtle disabled:text-tertiary disabled:cursor-not-allowed';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function Input({ invalid = false, className, ...rest }: InputProps) {
  return (
    <input
      className={cn(
        inputBase,
        'h-9',
        invalid ? 'border-status-danger' : 'border-border-default',
        className,
      )}
      {...rest}
    />
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function Textarea({ invalid = false, className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cn(
        inputBase,
        'min-h-[80px] py-2',
        invalid ? 'border-status-danger' : 'border-border-default',
        className,
      )}
      {...rest}
    />
  );
}

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

export function Label({ required, className, children, ...rest }: LabelProps) {
  return (
    <label className={cn('mb-1 block text-[13px] font-medium text-primary', className)} {...rest}>
      {children}
      {required ? <span className="ml-0.5 text-status-danger">*</span> : null}
    </label>
  );
}

export interface FormFieldProps {
  label?: string;
  required?: boolean;
  helpText?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  required,
  helpText,
  error,
  htmlFor,
  children,
  className,
}: FormFieldProps) {
  // Patch 5 — accessibility fix. The audit found 41 of 52 FormField uses
  // didn't pass `htmlFor`, leaving labels unassociated with their inputs.
  // Now we auto-generate a stable id when no htmlFor is given and inject it
  // into the child input via cloneElement. Existing callers that DO pass
  // htmlFor (and set the matching id themselves on the child) still win:
  // we don't overwrite an explicit id.
  const generatedId = useId();
  const effectiveId = htmlFor ?? generatedId;
  // Clone the child to add the id, but only when (a) it's a single React
  // element, (b) the caller didn't already pass `id`. Otherwise just leave
  // children alone — at worst a11y stays at status-quo for that field.
  let resolvedChildren: ReactNode = children;
  if (!htmlFor && isValidElement(children)) {
    const child = children as ReactElement<{ id?: string }>;
    if (child.props.id === undefined) {
      resolvedChildren = cloneElement(child, { id: effectiveId });
    }
  }
  return (
    <div className={cn('mb-4', className)}>
      {label ? (
        <Label htmlFor={effectiveId} required={required}>
          {label}
        </Label>
      ) : null}
      {resolvedChildren}
      {error ? (
        <p className="mt-1 text-xs text-status-danger">{error}</p>
      ) : helpText ? (
        <p className="mt-1 text-xs text-secondary">{helpText}</p>
      ) : null}
    </div>
  );
}
